import { PayloadAction, createSlice } from "@reduxjs/toolkit";
import type { HeroBlog } from "../constants";
import type { BlogCardPost, BlogRegion, BlogWriter } from "@Pages/Blog/BlogHomeRevamp/types";
import { LATEST_TAB_ID } from "@Pages/Blog/BlogHomeRevamp/settings";

export type BlogPostSimple = {
  updatedAt?: string;
  id: number;
  name: string;
  slug: string;
  mainImage: {
    id: number;
    alt: string;
    url: string;
    filename?: string;
    mimeType?: string;
    filesize?: number;
    width?: number;
    height?: number;
  };
  category: {
    id: number;
    name: string;
    slug: string;
  };
  date: string;
  readTime: string;
};

export type Category = {
  id: number;
  name: string;
  slug: string;
};

export type BlogPartner = {
  id: string;
  image: {
    id: number;
    alt: string;
    url: string;
    filename?: string;
    mimeType?: string;
    filesize?: number;
    width?: number;
    height?: number;
  };
  link: string;
};

interface BlogHomeData {
  readyStatus: "invalid" | "request" | "success" | "failure";
  homeFeaturedBlogs: HeroBlog[];
  sidebarFeaturedBlogs: BlogPostSimple[];
  latestBlogs: BlogPostSimple[];
  categories: Category[];
  activeCategory: string;
  activeCategorySlug: string;
  categoryBlogs: BlogPostSimple[];
  categoryBlogsLoading: boolean;
  blogPartners: BlogPartner[];
  totalDocs: number;
  globalInventories: any[];
  error: any;
  revampHeroPosts: BlogCardPost[];
  revampTabPosts: Record<string, BlogCardPost[]>;
  revampActiveTab: string;
  revampRegionPosts: Record<string, BlogCardPost[]>;
  revampWriters: BlogWriter[];
  revampWritersSubtitle: string | null;
  revampRegionOrder: string[];
  revampRegionDescriptions: Record<string, string>;
  revampLiveRegionSlugs: string[];
  revampUsesHomeRegionFallback: boolean;
  /** URL slug (`germany`, …) CMS denied for region PDP this session. */
  revampRegionPageDeniedSlug: string | null;
  /** URL slug that passed CMS live gate and finished region PDP loadData. */
  revampRegionPageReadySlug: string | null;
  /** Resolved region model for the active region PDP (includes CMS-only countries). */
  revampRegionPageRegion: BlogRegion | null;
  revampRegionsByCountrySlug: Record<string, BlogRegion>;
}

export const initialState: BlogHomeData = {
  readyStatus: "invalid",
  homeFeaturedBlogs: [],
  sidebarFeaturedBlogs: [],
  latestBlogs: [],
  categories: [],
  activeCategory: "latest",
  activeCategorySlug: "",
  categoryBlogs: [],
  categoryBlogsLoading: false,
  blogPartners: [],
  totalDocs: 0,
  globalInventories: [],
  error: null,
  revampHeroPosts: [],
  revampTabPosts: {},
  revampActiveTab: LATEST_TAB_ID,
  revampRegionPosts: {},
  revampWriters: [],
  revampWritersSubtitle: null,
  revampRegionOrder: [],
  revampRegionDescriptions: {},
  revampLiveRegionSlugs: [],
  revampUsesHomeRegionFallback: false,
  revampRegionPageDeniedSlug: null,
  revampRegionPageReadySlug: null,
  revampRegionPageRegion: null,
  revampRegionsByCountrySlug: {},
};

const blogHomeSlice = createSlice({
  name: "blogHomeData",
  initialState,
  reducers: {
    updateBlogHomeData: (state: BlogHomeData, { payload }: PayloadAction<any>) => {
      state.homeFeaturedBlogs = payload?.homeFeaturedBlogs || [];
      state.sidebarFeaturedBlogs = payload?.sidebarFeaturedBlogs || [];
      state.latestBlogs = payload?.latestBlogs || [];
      state.categories = payload?.categories || [];
      state.blogPartners = payload?.blogPartners || [];
      state.totalDocs = payload?.totalDocs || 0;
      if (payload?.globalInventories !== undefined) {
        state.globalInventories = payload.globalInventories;
      }
    },
    updateBlogHomeReadyStatus: (
      state: BlogHomeData,
      { payload }: PayloadAction<"invalid" | "request" | "success" | "failure">,
    ) => {
      state.readyStatus = payload;
    },
    updateBlogHomeError: (state: BlogHomeData, { payload }: PayloadAction<any>) => {
      state.error = payload;
    },
    setActiveCategory: (
      state: BlogHomeData,
      { payload }: PayloadAction<{ name: string; slug: string }>,
    ) => {
      state.activeCategory = payload.name;
      state.activeCategorySlug = payload.slug;
    },
    updateCategoryBlogs: (state: BlogHomeData, { payload }: PayloadAction<BlogPostSimple[]>) => {
      state.categoryBlogs = payload;
      state.categoryBlogsLoading = false;
    },
    setCategoryBlogsLoading: (state: BlogHomeData, { payload }: PayloadAction<boolean>) => {
      state.categoryBlogsLoading = payload;
    },
    updateBlogHomeRevampData: (
      state: BlogHomeData,
      {
        payload,
      }: PayloadAction<{
        heroPosts: BlogCardPost[];
        latestPosts: BlogCardPost[];
        regionPosts: Record<string, BlogCardPost[]>;
        writers: BlogWriter[];
        writersSubtitle: string | null;
        regionOrder: string[];
        regionDescriptions: Record<string, string>;
        liveRegionSlugs: string[];
        usesHomeRegionFallback: boolean;
        regionsByCountrySlug: Record<string, BlogRegion>;
      }>,
    ) => {
      state.revampHeroPosts = payload.heroPosts;
      state.revampTabPosts = { [LATEST_TAB_ID]: payload.latestPosts };
      state.revampActiveTab = LATEST_TAB_ID;
      state.revampRegionPosts = payload.regionPosts;
      state.revampWriters = payload.writers;
      state.revampWritersSubtitle = payload.writersSubtitle;
      state.revampRegionOrder = payload.regionOrder;
      state.revampLiveRegionSlugs = payload.liveRegionSlugs;
      state.revampUsesHomeRegionFallback = payload.usesHomeRegionFallback;
      state.revampRegionPageDeniedSlug = null;
      state.revampRegionPageReadySlug = null;
      state.revampRegionPageRegion = null;
      state.revampRegionsByCountrySlug = payload.regionsByCountrySlug;
      state.revampRegionDescriptions = {
        ...state.revampRegionDescriptions,
        ...payload.regionDescriptions,
      };
    },
    setRevampRegionPageDeniedSlug: (
      state: BlogHomeData,
      { payload }: PayloadAction<string | null>,
    ) => {
      state.revampRegionPageDeniedSlug = payload;
      if (payload && state.revampRegionPageReadySlug === payload) {
        state.revampRegionPageReadySlug = null;
      }
    },
    setRevampRegionPageReadySlug: (
      state: BlogHomeData,
      { payload }: PayloadAction<{ pageSlug: string; region: BlogRegion } | null>,
    ) => {
      state.revampRegionPageReadySlug = payload?.pageSlug ?? null;
      state.revampRegionPageRegion = payload?.region ?? null;
    },
    updateRevampRegionDescription: (
      state: BlogHomeData,
      { payload }: PayloadAction<{ countrySlug: string; description: string }>,
    ) => {
      state.revampRegionDescriptions[payload.countrySlug] = payload.description;
    },
    setRevampActiveTab: (state: BlogHomeData, { payload }: PayloadAction<string>) => {
      state.revampActiveTab = payload;
    },
    updateRevampTabPosts: (
      state: BlogHomeData,
      { payload }: PayloadAction<{ tabId: string; posts: BlogCardPost[] }>,
    ) => {
      state.revampTabPosts[payload.tabId] = payload.posts;
    },
    resetBlogHomeData: (state: BlogHomeData) => {
      state.readyStatus = "invalid";
      state.homeFeaturedBlogs = [];
      state.sidebarFeaturedBlogs = [];
      state.latestBlogs = [];
      state.categories = [];
      state.activeCategory = "latest";
      state.activeCategorySlug = "";
      state.categoryBlogs = [];
      state.categoryBlogsLoading = false;
      state.blogPartners = [];
      state.totalDocs = 0;
      state.globalInventories = [];
      state.error = null;
      state.revampHeroPosts = [];
      state.revampTabPosts = {};
      state.revampActiveTab = LATEST_TAB_ID;
      state.revampRegionPosts = {};
      state.revampWriters = [];
      state.revampWritersSubtitle = null;
      state.revampRegionOrder = [];
      state.revampRegionDescriptions = {};
      state.revampLiveRegionSlugs = [];
      state.revampUsesHomeRegionFallback = false;
      state.revampRegionPageDeniedSlug = null;
      state.revampRegionPageReadySlug = null;
      state.revampRegionPageRegion = null;
      state.revampRegionsByCountrySlug = {};
    },
  },
});

export const {
  updateBlogHomeData,
  updateBlogHomeReadyStatus,
  updateBlogHomeError,
  setActiveCategory,
  updateCategoryBlogs,
  setCategoryBlogsLoading,
  resetBlogHomeData,
  updateBlogHomeRevampData,
  setRevampActiveTab,
  updateRevampTabPosts,
  updateRevampRegionDescription,
  setRevampRegionPageDeniedSlug,
  setRevampRegionPageReadySlug,
} = blogHomeSlice.actions;

export default blogHomeSlice.reducer;
